import { useEffect, useState } from "react";
import moment from "moment";
import { formatCurrency } from "../util/Helpers";
import PaymentForm from "./PaymentForm";
import {
  NAVY,
  MUTED,
  HAIRLINE,
  SectionLabel,
  PillBadge,
  InfoRow,
  displayFont,
} from "../FlightFinalOffer/ui";

// Pay step for a held fare. Everything shown comes from our transaction
// row: `amount` is the grand total charged, metadata.amount / fee are the
// fare and add-ons captured at checkout, metadata.flightHold is the
// airline's hold response (PNR, hold deadline).

const leadTraveler = (trans: any) => {
  const travelers = trans?.metadata?.travelers;
  if (!Array.isArray(travelers) || travelers.length === 0) return "-";
  const p = travelers[0]?.passport || {};
  const name = [p.firstname, p.lastname].filter(Boolean).join(" ") || "-";
  return travelers.length > 1 ? `${name} +${travelers.length - 1}` : name;
};

// "2h 14m left" until the airline releases the hold.
const useTimeLeft = (expiry: Date | null) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiry) return;
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [expiry]);

  if (!expiry) return null;
  const ms = expiry.getTime() - now;
  if (ms <= 0) return "Expired";
  const d = moment.duration(ms);
  const days = Math.floor(d.asDays());
  if (days > 0) return `${days}d ${d.hours()}h left`;
  return `${d.hours()}h ${d.minutes()}m left`;
};

const Icon = ({ path }: { path: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={path} />
  </svg>
);

const Page3 = ({ trans, expiry, onPaid }: { trans: any; expiry: Date | null; onPaid: () => void }) => {
  const currency = trans?.currency;
  const total = Number(trans?.amount) || 0;
  const fare = Number(trans?.metadata?.amount ?? total) || 0;
  const addOns = Number(trans?.metadata?.fee) || Math.max(total - fare, 0);
  const pnr = trans?.metadata?.flightHold?.providerRef;
  const timeLeft = useTimeLeft(expiry);

  return (
    <div className="max-w-[640px] mx-auto">
      {/* Held booking */}
      <div className="co-card p-5 md:p-6 mb-4">
        <SectionLabel action={<PillBadge>On hold</PillBadge>}>Your held booking</SectionLabel>

        <div
          style={{
            fontFamily: displayFont,
            fontSize: 20,
            fontWeight: 700,
            color: NAVY,
            paddingBottom: 14,
            borderBottom: `1.5px solid ${HAIRLINE}`,
            marginBottom: 14,
          }}
        >
          {trans?.description || "Your held flight"}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoRow
            icon={<Icon path="M12 8v4l3 3M12 22a10 10 0 100-20 10 10 0 000 20z" />}
            label="Hold expires"
            value={
              expiry ? (
                <span>
                  {moment(expiry).format("ddd, D MMM · HH:mm")}
                  {timeLeft && (
                    <span style={{ color: MUTED, fontWeight: 500 }}> · {timeLeft}</span>
                  )}
                </span>
              ) : (
                "See your email"
              )
            }
          />
          <InfoRow
            icon={<Icon path="M20 7H4a1 1 0 00-1 1v8a1 1 0 001 1h16a1 1 0 001-1V8a1 1 0 00-1-1zM8 11h8M8 13h5" />}
            label="Airline PNR"
            value={pnr || "-"}
          />
          <InfoRow
            icon={<Icon path="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />}
            label="Traveler"
            value={leadTraveler(trans)}
          />
          <InfoRow
            icon={<Icon path="M4 4h16v16H4zM4 9h16M9 4v16" />}
            label="Booking reference"
            value={trans?.systemRef}
          />
        </div>
      </div>

      {/* Price details */}
      <div className="co-card p-5 md:p-6 mb-4">
        <SectionLabel>Price details</SectionLabel>
        <div className="space-y-2" style={{ fontSize: 14 }}>
          <div className="flex justify-between">
            <span style={{ color: MUTED }}>Fare</span>
            <span style={{ fontWeight: 600, color: NAVY }}>{formatCurrency(fare, currency)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: MUTED }}>Add-ons</span>
            <span style={{ fontWeight: 600, color: NAVY }}>{formatCurrency(addOns, currency)}</span>
          </div>
          <div className="flex justify-between pt-2" style={{ borderTop: `1.5px solid ${HAIRLINE}` }}>
            <span style={{ fontWeight: 700, color: NAVY }}>Total to pay</span>
            <span style={{ fontWeight: 800, color: NAVY }}>{formatCurrency(total, currency)}</span>
          </div>
        </div>
      </div>

      <PaymentForm trans={trans} onPaid={onPaid} />

      <p className="text-xs text-center mt-5" style={{ color: MUTED }}>
        Your ticket is issued once payment is confirmed. By paying, you agree with the{" "}
        <a href="/terms" style={{ color: NAVY, fontWeight: 600 }}>
          terms of service
        </a>{" "}
        and{" "}
        <a href="/policy" style={{ color: NAVY, fontWeight: 600 }}>
          privacy policy
        </a>{" "}
        of Stavigo.
      </p>
    </div>
  );
};
export default Page3;
